import React, { useState, useEffect, useRef } from "react";
import { FlightSearchState, TripType } from "./types";
import Header from "./Header";
import HeroBanner from "./HeroBanner";
import BookingWidget from "./BookingWidget";
import LoadingScreen from "./LoadingScreen";
import { useFlightSearch } from "../../hooks/useFlightSearch";
import { SearchParams, TravelClass } from "../../types/flight";
import { saveFormState, getFormState } from "../../utils/storageUtils";
import { useMockMode } from "../../hooks/useMockMode";
import { buildMockPaResponse } from "../../services/mockOffers";
import {
    LoyaltyLinkRecord,
    deriveWireLoyaltyLink,
    readAndClearLoyaltyGrantAttemptFlag,
    readAndClearToastFlag,
    shouldShowLoyaltyScopeDeniedNotice,
} from "../../services/mockLoyalty";
import {
    useLinkedTravelerState,
    waitForLoyaltyGrant,
} from "../../hooks/linkedTravelerStore";
import LoyaltyLinkModal from "./LoyaltyLinkModal";
import LoyaltyScopeDeniedNotice from "./LoyaltyScopeDeniedNotice";
import LoyaltySyncToast from "./LoyaltySyncToast";

// MOVMO-542 (notice-race fix): how long to wait for the landing's GET /me
// read (useLinkedTraveler) to bring in a `loyalty` group before deciding
// whether to show the denied-scope notice — see the grant-attempt effect below.
const GRANT_NOTICE_SETTLE_MS = 8000;

const PhilippineAirlines: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { navigateToResults } = useFlightSearch();
    // Capture ?mock=true from the URL on the landing page so subsequent
    // navigations (results, shopping cart) inherit the flag from sessionStorage.
    const mockMode = useMockMode();

    // MOVMO-541 loyalty-in-consent: the primary link/join decision now
    // happens on the Movmo consent screen (auth-ui); the connect callback
    // queues a one-shot toast flag that this landing page consumes below.
    // The on-site modal survives only as the late-link fallback for
    // travelers who chose "Not now" at consent — opened from the header's
    // "Link Mabuhay Miles" CTA via the event. MOVMO-567: both paths are
    // transition theater only now — the resting Mabuhay Miles state
    // (header chip, trip-selector) is wire-authoritative via
    // useLoyaltySession.ts and is not touched here.
    const [loyaltyModalOpen, setLoyaltyModalOpen] = useState(false);
    const [justLinked, setJustLinked] = useState<LoyaltyLinkRecord | null>(
        null,
    );
    // MOVMO-542 denied-scope follow-up: shown when the grant-half
    // interstitial's connect ceremony was launched but the traveler lands
    // back with loyalty.read still ungranted — see readAndClearLoyaltyGrantAttemptFlag's
    // effect below and mockLoyalty.ts's LOYALTY_GRANT_ATTEMPT_FLAG_KEY comment.
    const [loyaltyScopeDenied, setLoyaltyScopeDenied] = useState(false);
    const { traveler } = useLinkedTravelerState("pa");

    // Consume the callback's one-shot toast flag in an effect, NOT a state
    // initializer: StrictMode double-invokes initializers, and the removeItem
    // side effect made the second invocation read null (live finding).
    const consumedToastFlag = useRef(false);
    useEffect(() => {
        if (consumedToastFlag.current) return;
        consumedToastFlag.current = true;
        const record = readAndClearToastFlag();
        if (record) setJustLinked(record);
    }, []);

    // MOVMO-542 denied-scope follow-up: one-shot, same StrictMode-safe
    // pattern as the toast flag above (effect, not initializer). The
    // grant-attempt flag only tells us the ceremony was LAUNCHED — it
    // can't tell undecided (delta consent -> grant -> wire has loyalty)
    // apart from re-declined (re-ask shown again -> traveler says no again
    // -> wire still has none) on its own.
    //
    // NOTICE-RACE FIX: sessionStorage can already hold a linked but
    // loyalty-less snapshot from before the ceremony, and the fresh /me read
    // (carrying the just-granted `loyalty` group) lands a moment after this
    // effect fires. waitForLoyaltyGrant waits specifically for a `loyalty`
    // group to appear (re-checked on every SYNC_EVENT broadcast) instead of
    // resolving on any existing snapshot. The flag is still consumed exactly
    // once (ref guard); only the SHOW decision is deferred.
    const consumedGrantAttempt = useRef(false);
    useEffect(() => {
        if (consumedGrantAttempt.current) return;
        consumedGrantAttempt.current = true;
        if (!readAndClearLoyaltyGrantAttemptFlag()) return;

        let cancelled = false;
        waitForLoyaltyGrant("pa", GRANT_NOTICE_SETTLE_MS).then((settled) => {
            if (cancelled) return;
            const loyaltyPresent =
                deriveWireLoyaltyLink(settled?.loyalty) !== null;
            if (shouldShowLoyaltyScopeDeniedNotice(true, loyaltyPresent)) {
                setLoyaltyScopeDenied(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    // Belt-and-braces: if the notice is already showing and a LATER wire
    // update (any /me or loyalty-link response, on this page or a
    // background retry) brings in a non-null loyalty group, dismiss it
    // automatically rather than leaving a stale "stayed off" notice next
    // to a header that now shows the traveler linked.
    useEffect(() => {
        const loyaltyPresent = deriveWireLoyaltyLink(traveler?.loyalty) !== null;
        if (loyaltyScopeDenied && loyaltyPresent) {
            setLoyaltyScopeDenied(false);
        }
    }, [traveler, loyaltyScopeDenied]);

    useEffect(() => {
        const openModal = () => setLoyaltyModalOpen(true);
        window.addEventListener("movmo:open-loyalty-link", openModal);
        return () =>
            window.removeEventListener("movmo:open-loyalty-link", openModal);
    }, []);

    const handleLoyaltyLinked = (record: LoyaltyLinkRecord) => {
        // Deliberately NOT closing the modal here — the ceremony's "You're
        // all set" screen (name + member number) is the demo's payoff shot;
        // the modal's own Done button closes it. MOVMO-567: this only shows
        // the confirmation toast (theater) — it does NOT flip the header
        // chip / miles toggle, which are wire-authoritative
        // (useLoyaltySession.ts) and only show linked once the traveler's
        // Movmo loyalty.read grant actually returns a program.
        setJustLinked(record);
    };

    // Generate default dates
    const getDefaultDates = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const weekFromTomorrow = new Date(tomorrow);
        weekFromTomorrow.setDate(tomorrow.getDate() + 7);

        const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];

        const formatDate = (date: Date) => {
            const day = date.getDate();
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        };

        return {
            departureDate: formatDate(tomorrow),
            returnDate: formatDate(weekFromTomorrow),
        };
    };

    const defaultDates = getDefaultDates();

    // "26-Nov-2025" → Date. The DatePicker disables past days so a stale
    // saved date renders the field unusable until the user opens the
    // picker; refreshing here keeps the field interactive on every load.
    const parseFormattedDate = (s: string): Date | null => {
        const monthIdx: Record<string, number> = {
            Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
            Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
        };
        const parts = s.split("-");
        if (parts.length !== 3) return null;
        const day = parseInt(parts[0], 10);
        const month = monthIdx[parts[1]];
        const year = parseInt(parts[2], 10);
        if (isNaN(day) || month === undefined || isNaN(year)) return null;
        return new Date(year, month, day);
    };
    const isPastDate = (s: string): boolean => {
        const d = parseFormattedDate(s);
        if (!d) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return d < today;
    };

    // Try to restore form state from localStorage, otherwise use defaults
    const [searchState, setSearchState] = useState<FlightSearchState>(() => {
        const savedState = getFormState();
        if (savedState) {
            // Replace stale dates with fresh defaults; preserve everything else
            // (origin, destination, passenger counts).
            if (
                isPastDate(savedState.departureDate) ||
                (savedState.tripType === "round-trip" &&
                    isPastDate(savedState.returnDate))
            ) {
                return {
                    ...savedState,
                    departureDate: defaultDates.departureDate,
                    returnDate: defaultDates.returnDate,
                };
            }
            return savedState;
        }
        return {
            tripType: "round-trip" as TripType,
            departure: "Manila-MNL",
            destination: "Cebu-CEB",
            departureDate: defaultDates.departureDate,
            returnDate: defaultDates.returnDate,
            passengers: "1 pax, All Cabins",
            bookWithMiles: false,
            passengerDetails: {
                adults: 1,
                children: 0,
                infants: 0,
                seniorCitizens: 0,
                personsWithDisability: 0,
                overseasFilipinoWorkers: 0,
            },
        };
    });

    // Save form state whenever it changes
    useEffect(() => {
        saveFormState(searchState);
    }, [searchState]);

    const handleSearchStateChange = (updates: Partial<FlightSearchState>) => {
        setSearchState((prev) => ({ ...prev, ...updates }));
    };

    const handleSearch = async () => {
        console.log("Searching flights with:", searchState);
        setIsLoading(true);

        // Parse location codes from the format "Manila-MNL" -> "MNL"
        const getLocationCode = (location: string): string => {
            const parts = location.split("-");
            return parts[parts.length - 1];
        };

        // Get city names for display
        const getLocationName = (location: string): string => {
            const parts = location.split("-");
            return parts[0];
        };

        // Convert date format from "13-Sep-2025" to "2025-09-13"
        const convertDateFormat = (dateStr: string): string => {
            const parts = dateStr.split("-");
            const day = parts[0].padStart(2, "0");
            const monthMap: { [key: string]: string } = {
                Jan: "01",
                Feb: "02",
                Mar: "03",
                Apr: "04",
                May: "05",
                Jun: "06",
                Jul: "07",
                Aug: "08",
                Sep: "09",
                Oct: "10",
                Nov: "11",
                Dec: "12",
            };
            const month = monthMap[parts[1]];
            const year = parts[2];
            return `${year}-${month}-${day}`;
        };

        // Map cabin class to the GDS-style cabin code
        const mapTravelClass = (cabin: string): string | undefined => {
            const cabinPart = cabin.split(", ")[1]; // Get "All Cabins" from "1 pax, All Cabins"
            switch (cabinPart) {
                case "Economy":
                    return "ECONOMY";
                case "Comfort":
                    return "PREMIUM_ECONOMY";
                case "Business":
                    return "BUSINESS";
                case "All Cabins":
                default:
                    return undefined; // Don't include travelClass param for "All Cabins"
            }
        };

        // Parse passenger counts from the detailed breakdown
        const getPassengerCounts = () => {
            const details = searchState.passengerDetails || {
                adults: 1,
                children: 0,
                infants: 0,
                seniorCitizens: 0,
                personsWithDisability: 0,
                overseasFilipinoWorkers: 0,
            };

            // Calculate total adults (including senior citizens, PWD, and OFW)
            const totalAdults =
                details.adults +
                details.seniorCitizens +
                details.personsWithDisability +
                details.overseasFilipinoWorkers;

            return {
                adults: totalAdults,
                children: details.children,
                infants: details.infants,
            };
        };

        try {
            const travelClass = mapTravelClass(searchState.passengers);
            const passengerCounts = getPassengerCounts();
            const searchParams: SearchParams = {
                originLocationCode: getLocationCode(searchState.departure),
                destinationLocationCode: getLocationCode(
                    searchState.destination
                ),
                departureDate: convertDateFormat(searchState.departureDate),
                adults: passengerCounts.adults,
                includedAirlineCodes: "PR", // Always filter for Philippine Airlines
                currencyCode: "USD",
                max: 20,
            };

            // Add children and infants if they exist
            if (passengerCounts.children > 0) {
                searchParams.children = passengerCounts.children;
            }
            if (passengerCounts.infants > 0) {
                searchParams.infants = passengerCounts.infants;
            }

            // Only add travelClass if it's not "All Cabins"
            if (travelClass) {
                searchParams.travelClass = travelClass as TravelClass;
            }

            // Add return date for round-trip
            if (
                searchState.tripType === "round-trip" &&
                searchState.returnDate
            ) {
                searchParams.returnDate = convertDateFormat(
                    searchState.returnDate
                );
            }

            console.log("Search params:", searchParams);
            // Search is always synthesised client-side: 4 offers in the
            // PSS flight-offer shape the existing PA results page renders.
            // The real Amadeus test API was retired (MOVMO-590), so no
            // network request is made here in any mode.
            const response = buildMockPaResponse({
                origin: searchParams.originLocationCode,
                destination: searchParams.destinationLocationCode,
                departureDate: searchParams.departureDate,
                returnDate: searchParams.returnDate,
                adults: searchParams.adults,
                children: searchParams.children || 0,
                infants: searchParams.infants || 0,
                travelClass: searchParams.travelClass,
            });
            console.log("Search response:", response);

            // Navigate to results with data saved in sessionStorage
            if (response.data && response.data.length > 0) {
                const passengerCounts = getPassengerCounts();
                navigateToResults(
                    {
                        origin: getLocationName(searchState.departure),
                        destination: getLocationName(searchState.destination),
                        tripType: searchState.tripType,
                        departureDate: searchState.departureDate,
                        returnDate: searchState.returnDate,
                        adults: passengerCounts.adults,
                        children: passengerCounts.children,
                        infants: passengerCounts.infants,
                        travelClass: travelClass,
                    },
                    response.data,
                    response.dictionaries
                );
            } else {
                // No results found
                setIsLoading(false);
                alert("No flights found for your search criteria.");
            }
        } catch (error) {
            console.error("Error searching flights:", error);
            setIsLoading(false);
            alert(
                "An error occurred while searching for flights. Please try again."
            );
        }
    };

    // Show loading screen while searching
    if (isLoading) {
        return <LoadingScreen />;
    }

    // Always show booking page (results are on a separate route now)
    return (
        <div
            className="min-h-screen relative"
            style={{ backgroundColor: "#fcfcfc" }}
        >
            <Header
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
            />

            <HeroBanner airline="pa" />

            <BookingWidget
                searchState={searchState}
                onSearchStateChange={handleSearchStateChange}
                onSearch={handleSearch}
            />

            <LoyaltyLinkModal
                open={loyaltyModalOpen}
                onClose={() => setLoyaltyModalOpen(false)}
                onLinked={handleLoyaltyLinked}
            />
            <LoyaltySyncToast
                linkedEvent={
                    justLinked
                        ? {
                              path: justLinked.path,
                              imported: justLinked.importConsent,
                              linkedAt: justLinked.linkedAt,
                          }
                        : null
                }
            />
            <LoyaltyScopeDeniedNotice
                show={loyaltyScopeDenied}
                onDismiss={() => setLoyaltyScopeDenied(false)}
            />
        </div>
    );
};

export default PhilippineAirlines;
